import React from 'react';
import { Lock, Eye, Share2, Shield, Database, Bell } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: `We collect information you provide directly to us, such as when you create an account, place an order, or contact us. This includes your name, email address, phone number, shipping address, and payment information.`,
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: `We use your information to process your orders, communicate with you about your purchases, improve our services, send you promotional offers (with your consent), and comply with legal obligations.`,
    },
    {
      icon: Share2,
      title: 'Information Sharing',
      content: `We do not sell your personal information. We may share information with trusted third parties who assist us in operating our business, such as payment processors and delivery partners, only to the extent necessary to provide our services.`,
    },
    {
      icon: Shield,
      title: 'Data Security',
      content: `We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.`,
    },
    {
      icon: Lock,
      title: 'Cookies & Tracking',
      content: `We use cookies and similar tracking technologies to enhance your browsing experience, analyze site traffic, and understand where our visitors come from. You can control cookies through your browser settings.`,
    },
    {
      icon: Bell,
      title: 'Your Rights',
      content: `You have the right to access, correct, or delete your personal information. You may also object to or restrict certain processing activities. Contact us to exercise these rights.`,
    },
    {
      icon: Database,
      title: 'Data Retention',
      content: `We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements.`,
    },
    {
      icon: Shield,
      title: 'Children\'s Privacy',
      content: `Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will take steps to delete it.`,
    },
    {
      icon: Eye,
      title: 'Third-Party Links',
      content: `Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites.`,
    },
    {
      icon: Lock,
      title: 'Changes to This Policy',
      content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date.`,
    },
  ];

  return (
    <div className="bg-dark-dark dark:bg-white transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent via-accent-light to-primary opacity-90" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Lock className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Last updated: January 15, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-300 dark:text-gray-600 mb-8 text-center transition-colors duration-300">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={index}
                className="border-l-4 border-accent dark:border-primary pl-6 py-4 transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center transition-colors duration-300">
                    <section.icon className="w-5 h-5 text-accent dark:text-primary transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white dark:text-dark-dark transition-colors duration-300">
                    {section.title}
                  </h3>
                </div>
                <p className="text-gray-300 dark:text-gray-600 leading-relaxed transition-colors duration-300">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Consent Section */}
          <div className="mt-12 p-6 bg-dark-light dark:bg-gray-50 rounded-lg transition-colors duration-300">
            <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-3 transition-colors duration-300">
              Your Consent
            </h3>
            <p className="text-gray-300 dark:text-gray-600 mb-4 transition-colors duration-300">
              By using our website and services, you consent to our Privacy Policy.
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 transition-colors duration-300">
              You may withdraw your consent at any time by contacting us or adjusting your account settings.
            </p>
          </div>

          {/* Contact Information */}
          <div className="mt-8 p-6 bg-accent/10 dark:bg-primary/10 border border-accent/30 dark:border-primary/30 rounded-lg transition-colors duration-300">
            <h3 className="text-lg font-semibold text-white dark:text-dark-dark mb-3 transition-colors duration-300">
              Contact Information
            </h3>
            <p className="text-gray-300 dark:text-gray-600 mb-2 transition-colors duration-300">
              If you have questions about this Privacy Policy or our privacy practices, please contact:
            </p>
            <p className="mt-2">
              <a
                href="mailto:privacy@angelcite.com"
                className="text-accent dark:text-primary hover:underline transition-colors duration-300"
              >
                privacy@angelcite.com
              </a>
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-4 transition-colors duration-300">
              Angel Cite<br />
              123 Farm Road<br />
              Harare, Zimbabwe
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;